import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { apiUrl } from 'constants/envConfig';
import { logout, setAuth } from 'store/auth-slice';
import { RootState } from 'store/index';

const rawBaseQuery = fetchBaseQuery({
    baseUrl: apiUrl,
    prepareHeaders: (headers, { getState }) => {
        const token = (getState() as RootState).auth.accessToken;
        if (token) {
            headers.set('authorization', `Bearer ${token}`);
        }
        return headers;
    },
});
const mutex = new Mutex();

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions,
) => {
    let result = await rawBaseQuery(args, api, extraOptions);

    if (result.error?.status === 401) {
        const state = api.getState() as RootState;
        const refreshToken = state.auth.refreshToken;

        if (!refreshToken) {
            api.dispatch(logout());
            return result;
        }

        await mutex.runExclusive(async () => {
            const stateAfterLock = api.getState() as RootState;

            if (stateAfterLock.auth.accessToken !== state.auth.accessToken) {
                result = await rawBaseQuery(args, api, extraOptions);
                return;
            }

            const refreshResponse = await rawBaseQuery(
                {
                    url: '/auth/refreshToken',
                    method: 'POST',
                    body: { refreshToken, sessionId: state.auth.sessionId },
                },
                api,
                extraOptions,
            );

            if (refreshResponse.data) {
                const { accessToken, refreshToken } = refreshResponse.data as {
                    accessToken: string;
                    refreshToken: string;
                };

                api.dispatch(
                    setAuth({
                        accessToken,
                        refreshToken,
                        sessionId: state.auth.sessionId,
                    }),
                );

                result = await rawBaseQuery(args, api, extraOptions);
            } else {
                await rawBaseQuery(
                    {
                        url: '/auth/logout',
                        method: 'POST',
                        body: { refreshToken, sessionId: state.auth.sessionId },
                    },
                    api,
                    extraOptions,
                );
                api.dispatch(logout());
            }
        });
    }

    return result;
};

export default createApi({
    baseQuery: baseQueryWithReauth,
    endpoints: () => ({}),
});
